/* eslint-disable react/react-in-jsx-scope */
import { useState, CSSProperties } from 'react'
import { PulseLoader } from 'react-spinners'

import APIService from '../../lib/api.service';
import "./forms.scss";

type State = {
  loading: boolean;
  ok?: boolean;
  msg?: string;
  routedTo?: string;
};

export default function SmartBizForm() {
  const [state, setState] = useState<State>({ loading: false });

    const [showForm, setShowForm] = useState(true);
    const [showSuccessPrompt, setShowSuccessPrompt] = useState(false);
    const [showFailedPrompt, setShowFailedPrompt] = useState(false);
  
    // Loading spinner configuration
    const [showLoader, setShowLoader] = useState(false);
    const color = "#FFA500"
    const override: CSSProperties = {
      display: "block",
      margin: "0 auto",
      borderColor: "orange",
    }
  
    const loaderContainerStyle: CSSProperties = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '2rem',
      width: '100%'
    }


  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);

    setShowLoader(true); // Set spinner
    setShowForm(false);
    setShowSuccessPrompt(false);
    setShowFailedPrompt(false);

    const street2 = form.get("street2");
    const addressParts = [
      form.get("street"),
      street2 ? street2 : null,
      form.get("city")
    ].filter(Boolean).join(", ");

    const payload = {
      kind: "smartbiz",
      name: `${form.get("firstName")} ${form.get("lastName")}`,
      email: form.get("email"),
      phone: `${form.get("phone")} ${form.get("phoneExt") ? `ext. ${form.get("phoneExt")}` : ""}`.trim(),
      address: addressParts,
      businessName: form.get("businessName"),
      website: form.get("website"), // honeypot
    };

    // Honeypot hit → silently succeed
    if (payload.website) {
      setShowSuccessPrompt(true);
      setShowLoader(false);
      return;
    }

    try {
      const submit = await APIService.postSmartbizInfo(JSON.stringify(payload));

      setShowLoader(false);
      if (submit.status === 201) {
        setShowSuccessPrompt(true);
      } else if (submit.status === 500) {
        setShowFailedPrompt(true);
        setShowForm(true);
      }

    } catch (error) {
      setShowLoader(false);
      setShowFailedPrompt(true);
      setShowForm(true);
    }
  }

  return (
    <div className="page-wrap">
      <div className="form-shell">
        <form className="form-card" onSubmit={onSubmit}>
          {showLoader && (
            <div style={loaderContainerStyle}>
              <PulseLoader
                color={color}
                loading={showLoader}
                cssOverride={override}
                size={50}
                aria-label="Loading Spinner"
                data-testid="loader"
              />
            </div>
          )}

          {showSuccessPrompt && (
            <h3 className="headline__h3 color--marina-blue">
              Your interest has been submitted! Our SmartBiz team will reach out to you as soon as possible. If you have any questions, please contact us at <a href="mailto:smartbiz@citysidefiber.com"> smartbiz@citysidefiber.com.</a>
            </h3>
          )}

          {showFailedPrompt && (
            <h3 className="headline__h3 color--marina-blue">
              Hmm... it looks like we're having trouble submitting your inquiry. Try again in a couple minutes, and if that doesn't work, please contact us at <a href="mailto:smartbiz@citysidefiber.com"> smartbiz@citysidefiber.com.</a>
            </h3>
          )}

          {showForm && (
          <>
          {/* hidden honeypot */}
          <input
            name="website"
            type="text"
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            className="honeypot"
          />

          {/* Row 1: First/Last/Email */}
          <div className="row three-up">
            <div>
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                name="firstName"
                required
                placeholder="Jane"
                autoComplete="given-name"
              />
            </div>
            <div>
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                name="lastName"
                required
                placeholder="Doe"
                autoComplete="family-name"
              />
            </div>
            <div>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
          </div>

          {/* Street Address */}
          <div className="row">
            <label htmlFor="street">Street Address</label>
            <input
              id="street"
              name="street"
              required
              placeholder="123 Main St"
              autoComplete="address-line1"
            />
          </div>

          {/* City + State */}
          <div className="row two-up">
            {/* Ste./Unit */}
            <div className="row">
              <label htmlFor="street2">Ste. / Unit (optional)</label>
              <input
                id="street2"
                name="street2"
                placeholder="Ste. 204"
                autoComplete="address-line2"
              />
            </div>
            <div>
              <label htmlFor="city">City</label>
              <input
                id="city"
                name="city"
                required
                placeholder="City"
                autoComplete="address-level2"
              />
            </div>
          </div>

          {/* Phone + Ext */}
          <div className="row two-up">
            <div>
              <label htmlFor="phone">Phone</label>
              <input
                id="phone"
                name="phone"
                inputMode="tel"
                placeholder="5551234567"
                pattern="[0-9]{3}-?[0-9]{3}-?[0-9]{4}"
                title="Use format: 5551234567"
                required
                autoComplete="tel-national"
                maxLength={12}
              />
            </div>
            <div>
              <label htmlFor="phoneExt">Ext. (optional)</label>
              <input
                id="phoneExt"
                name="phoneExt"
                inputMode="numeric"
                placeholder="123"
                maxLength={6}
                pattern="^\d{1,6}$"
                title="Digits only"
                autoComplete="tel-extension"
              />
            </div>
          </div>

          {/* Business Name */}
          <div className="row">
            <label htmlFor="businessName">Business Name</label>
            <input
              id="businessName"
              name="businessName"
              required
              placeholder="Company LLC"
              autoComplete="organization"
            />
          </div>

          {/* Actions */}
          <div className="actions">
            <button className="btn-primary mtBold" disabled={state.loading}>
              {state.loading ? "Submitting…" : "Submit"}
            </button>
          </div>

          </>
          )}
        </form>
      </div>
    </div>
  );
}
